import { URLS, createJurisdictionHeaders } from '@/data';
import { useUserStore } from '@/hooks/use-user-store';
import { ErrorResponse } from '@/utils/errors/response-handler';
import { WalletSchema } from '@/utils/fetch-wallet-balance';
import { UseQueryOptions, useQuery } from '@tanstack/react-query';
import { differenceInDays } from 'date-fns';
import { z } from 'zod';

// can add all the auth user config error_codes here.
type AuthUserConfigErrorCodes = 'user_account_block';

export type AuthUserConfigErrorType = ErrorResponse<AuthUserConfigErrorCodes>;

export enum DueDiligence {
    EDD_SUCCESS = 'EDD Success',
    EDD_FAILED = 'EDD Failed',
    CDD = 'CDD',
}

export enum DueDiligenceReasons {
    MANDATORY = 'Mandatory',
    PASSIVE = 'Passive',
    UNKNOWN_SOURCE = 'Unknown Source',
}

export type DueDiligenceType = DueDiligence | null;
export type DueDiligenceReasonType = DueDiligenceReasons | null;

const AuthUserConfigSchema = z.object({
    data: z.object({
        config: z.object({
            USER_KYC: z.boolean(),
            HAS_USER_DEPOSITED: z.boolean(),
            HAS_USER_PURCHASED: z.boolean().optional(),
            FAILED_KYC_COUNT: z.number(),
            CAN_DEPOSIT: z.boolean().optional(),
            CAN_WITHDRAW: z.boolean().optional(),
            IS_BIOMETRIC_ENABLED: z.boolean(),
            GLOBAL_USER_COHORT: z.string(),
            GLOBAL_USER_EMAIL: z.string(),
            GLOBAL_USER_ID: z.string(),
            USER_DUE_DILIGENCE: z.nativeEnum(DueDiligence).nullable(),
            USER_DUE_DILIGENCE_REASON: z.nativeEnum(DueDiligenceReasons).nullable(),
            IDPV_ALLOWED_IN_KYC: z.boolean(),
            FORCE_IDPV: z.boolean().optional(),
            TRUSTLY_COMPLETED: z.boolean().optional(),
            APPSFLYER_ID: z.string().nullable().optional(),
            SITE_ID: z.number().nullable().optional(),
            IS_MFA_ENABLED: z.boolean().optional(),
            USER_IS_SELF_EXCLUDED: z.boolean().optional(),
            USER_CREATED_AT_TIMESTAMP_MS: z.number().optional(),
            IS_BELOW_AGE_LIMIT: z.boolean().optional(),
            POLYMARKET_KYC_VERIFIED: z.boolean().optional().catch(undefined),
            POLYMARKET_KYC_ELIGIBLE: z.boolean().optional().catch(undefined),
            POLYMARKET_KYC_PENDING: z.boolean().optional().catch(undefined),
            USER_TERMS_VERSIONS: z
                .array(
                    z.object({
                        vertical: z.string(),
                        accepted_and_updated: z.boolean(),
                    })
                )
                .optional(),
            MIN_GAMBLING_AGE: z.number().optional(),
            AVATAR_URL: z.string().nullable().optional(),
            USERNAME: z.string().nullable().optional(),
            // TODO: add additional fields when needed
        }),
    }),
    wallet: WalletSchema,
    fantasy_wallet: WalletSchema,
});
type AuthUserConfig = z.infer<typeof AuthUserConfigSchema>;

/**
 * Query function for fetching authenticated user config
 * GET /auth/user/config
 */
export const getAuthUserConfig = async () => {
    const response = await fetch(`${URLS.CHAMELON_API_URL}/auth/user/config`, {
        method: 'GET',
        headers: createJurisdictionHeaders(),
    });
    const data = await response.json();
    if (!response.ok) {
        throw data;
    }
    return AuthUserConfigSchema.parse(data);
};

export const authUserKeys = {
    all: ['auth-user'] as const,
    config: () => [...authUserKeys.all, 'config'] as const,
    info: () => [...authUserKeys.all, 'info'] as const,
    settings: () => [...authUserKeys.all, 'settings'] as const,
};

type UserAuthUserConfigQueryOptions<TData> = Omit<
    UseQueryOptions<AuthUserConfig, unknown, TData, ReturnType<typeof authUserKeys.config>>,
    'queryKey'
>;

/**
 * Custom react-query hook for fetching authenticated user config
 * @param options - react-query options object
 */
export const useAuthUserConfig = <TData = AuthUserConfig>(options?: UserAuthUserConfigQueryOptions<TData>) => {
    const sessionExpired = useUserStore(state => state.actions.sessionHasExpired);
    return useQuery({
        queryKey: authUserKeys.config(),
        queryFn: getAuthUserConfig,
        enabled: !sessionExpired(),
        staleTime: 1000 * 60 * 3,
        ...options,
    });
};

/**
 * Selector for checking if the user is KYC verified
 * Selector for checking if the user has deposited
 * Selector for user lifetime's KYC failures
 * @param resp - getAuthUserConfig response
 */
export const kycVerifiedSelector = (resp: AuthUserConfig) => resp.data.config.USER_KYC;
export const hasMadeFirstDepositSelector = (resp: AuthUserConfig) => resp.data.config.HAS_USER_DEPOSITED;
export const avatarUrlSelector = (resp: AuthUserConfig) => resp.data.config.AVATAR_URL;
export const usernameSelector = (resp: AuthUserConfig) => resp.data.config.USERNAME;
export const hasUserPurchasedSelector = (resp: AuthUserConfig) => resp.data.config.HAS_USER_PURCHASED;
export const isBiometricEnabledSelector = (resp: AuthUserConfig) => resp.data.config.IS_BIOMETRIC_ENABLED;
export const failedKycCountSelector = (resp: AuthUserConfig) => resp.data.config.FAILED_KYC_COUNT;
export const canWithdrawSelector = (resp: AuthUserConfig) => resp.data.config.CAN_WITHDRAW;
export const userIdSelector = (resp: AuthUserConfig) => resp.data.config.GLOBAL_USER_ID;
export const userDueDiligenceSelector = (resp: AuthUserConfig) => resp.data.config.USER_DUE_DILIGENCE;
export const isIdpvAllowedInKycSelector = (resp: AuthUserConfig) => resp.data.config.IDPV_ALLOWED_IN_KYC;
export const userDueDiligenceReasonSelector = (resp: AuthUserConfig) => resp.data.config.USER_DUE_DILIGENCE_REASON;
export const isUserBelowAgeLimitToPlaySelector = (resp: AuthUserConfig) => resp.data.config.IS_BELOW_AGE_LIMIT;
export const userTermsVersionSelector = (resp: AuthUserConfig) => resp.data.config.USER_TERMS_VERSIONS;
export const minGamblingAgeSelector = (resp: AuthUserConfig) => resp.data.config.MIN_GAMBLING_AGE;
export const isTrustlyCompletedSelector = (resp: AuthUserConfig) => resp.data.config.TRUSTLY_COMPLETED;
export const polymarketKycVerifiedSelector = (resp: AuthUserConfig) => resp.data.config.POLYMARKET_KYC_VERIFIED;
export const polymarketKycEligibleSelector = (resp: AuthUserConfig) => resp.data.config.POLYMARKET_KYC_ELIGIBLE;
export const polymarketKycPendingSelector = (resp: AuthUserConfig) => resp.data.config.POLYMARKET_KYC_PENDING;
export const userCreatedAtTimestampSelector = (resp: AuthUserConfig) => resp.data.config.USER_CREATED_AT_TIMESTAMP_MS;

/**
 * Selector to check if user was created after a specific release date
 * @param date - release date in string format (e.g., '2023-01-15')
 */

export const userCreatedAfterDateSelector = (date: string) => (resp: AuthUserConfig) => {
    const userCreatedAtTimestamp = resp.data.config.USER_CREATED_AT_TIMESTAMP_MS;
    if (!userCreatedAtTimestamp) {
        return false;
    }

    const userCreatedDate = new Date(userCreatedAtTimestamp);
    const releaseDateObj = new Date(date);

    // User is created after release date if the difference is positive (user created later)
    return differenceInDays(userCreatedDate, releaseDateObj) > 0;
};
